import { formatDistanceToNow } from 'date-fns'
import { Info } from 'lucide-react'
import type { VendorAdvisory, VendorName } from '@/lib/types'

/**
 * V5 Slice 3 — vendor advisory list rendered inside the Remediation card.
 *
 * Displays per-vendor fix versions for a single CVE. Input is already the
 * projected, non-suppressed list from `GET /api/vulnerabilities/:id`, but
 * we defensively filter again here so the admin edit page (which fetches the
 * full list including suppressed rows) can reuse the component for the CISO
 * preview without leaking suppressed rows.
 *
 * Returns `null` when no visible advisories — the Remediation card decides
 * whether to fall back to V3's single-button layout.
 */

const VENDOR_LABEL: Record<VendorName, string> = {
  msrc: 'Microsoft',
  redhat: 'Red Hat',
  ubuntu: 'Ubuntu',
  cisco: 'Cisco',
  paloalto: 'Palo Alto',
  suse: 'SUSE',
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'unknown'
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return 'unknown'
  return formatDistanceToNow(parsed, { addSuffix: true })
}

// Trim the trailing ":CVE-…" suffix off synthetic advisory IDs so the user sees
// e.g. "MSRC-2024-JAN" not "MSRC-2024-JAN:CVE-2024-0001". Real vendor advisory
// IDs without that suffix render unchanged.
function stripCveSuffix(advisoryId: string): string {
  const idx = advisoryId.lastIndexOf(':CVE-')
  return idx > 0 ? advisoryId.slice(0, idx) : advisoryId
}

export interface VendorAdvisoryListProps {
  advisories: VendorAdvisory[]
}

export function VendorAdvisoryList({ advisories }: VendorAdvisoryListProps) {
  const visible = advisories
    .filter((a) => a.suppressedAt == null)
    .slice()
    .sort((a, b) => {
      const at = a.publishedAt ? new Date(a.publishedAt).getTime() : 0
      const bt = b.publishedAt ? new Date(b.publishedAt).getTime() : 0
      if (at !== bt) return bt - at
      return a.vendor.localeCompare(b.vendor)
    })

  if (visible.length === 0) return null

  const lastSynced = visible.reduce<string | null>(
    (acc, a) => (acc == null || a.lastSyncedAt > acc ? a.lastSyncedAt : acc),
    null,
  )

  return (
    <div
      className="mt-3 space-y-2"
      data-testid="vendor-advisory-list"
      aria-label="Vendor advisories"
    >
      <h4 className="text-sm font-semibold text-foreground">
        Fix versions by vendor
      </h4>
      <ul className="space-y-2">
        {visible.map((a) => (
          <li
            key={a.id}
            data-testid="vendor-advisory-row"
            className="text-sm"
          >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-medium text-foreground">
                {VENDOR_LABEL[a.vendor]}
              </span>
              <span aria-hidden="true" className="text-muted-foreground">
                —
              </span>
              <a
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {stripCveSuffix(a.advisoryId)}
                <span aria-hidden="true"> ↗</span>
              </a>
            </div>
            {a.fixVersion && (
              <div className="pl-0.5 font-mono text-xs text-muted-foreground">
                {a.fixVersion}
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">
        {visible.length} advisor{visible.length === 1 ? 'y' : 'ies'} · Last
        synced {formatRelative(lastSynced)}
      </p>
      <p
        data-testid="vendor-advisory-pending-note"
        className="flex items-start gap-1.5 text-xs text-muted-foreground"
      >
        <Info
          aria-hidden="true"
          className="mt-0.5 h-3.5 w-3.5 shrink-0"
        />
        <span>
          Cisco PSIRT and Palo Alto Networks advisories — pending vendor API
          access.
        </span>
      </p>
    </div>
  )
}
